<script lang="ts">
  import { fade } from "svelte/transition";
  import { isPersonVariant } from "../../../../shared/utils/validation";
  import { FormInput } from "../../../../shared/lib/validation_next";
  import InputDropdown from "../../../../shared/components/InputDropdown.svelte";
  import ListNavigationItem from "../../../../shared/components/ListNavigationItem.svelte";
  import LoadingIndicator from "../../../../shared/components/LoadingIndicator.svelte";
  import { withApi } from "../../../../shared/lib/api-parkito";
  import { type Writable, writable } from "svelte/store";
  import { Toast } from "$components";
  import type { Debtor, IndexedDebtor } from "../../../../apis/parkito2";
  import type {
    ApiError,
    Debtor as OldDebtor,
  } from "../../../../apis/parkito";
  import { onMount } from "svelte";
  import { log } from "$shared/utils/misc";

  export let label: string;
  export let preSelectedDebtorId: string | undefined;
  export let selectedDebtor: OldDebtor | Debtor | undefined | null =
    undefined;
  export let selectedDebtorOutput: FormInput<OldDebtor | Debtor> | null =
    null;
  export let disabled: boolean = false;

  let debtorHits: number = 0;
  let selectedDebtorIdx: Writable<number | undefined> = writable(undefined);
  let debtorItems: string[] = [];
  let debtors: IndexedDebtor[] = [];
  let selectedDebtorIsLoading: boolean = false;

  const fetchDebtors = async (searchQuery: string = "") => {
    const args = {
      pagination: {
        page: 1,
        pageSize: 15,
      },
      query: searchQuery,
      siteId: null,
    };
    const res = await withApi((api) => {
      return api.debtorSearch(args);
    });

    if (res.isOk()) {
      debtors = res.value.items;
      debtorHits = res.value.totalCount;
      if (import.meta.env.DEV) {
        console.log("Debtors fetched", debtors.length);
      }
    } else {
      Toast.error("Kunde inte hämta parkörer");
    }
  };

  const fetchSelectedDebtor = async (id: string) => {
    selectedDebtorIsLoading = true;
    const res = await withApi((api) => {
      return api.debtorGet(id);
    });

    if (selectedDebtorOutput) {
      selectedDebtorOutput.set({
        hasChanged: true,
        res: res.mapErr((err: ApiError) => err.toString()),
      });
    }

    if (res.isOk()) {
      selectedDebtor = res.value;
    } else {
      Toast.error("Fel uppstod vid hämtning av parkör");
    }
    selectedDebtorIsLoading = false;
  };

  // Fetch data when mounted
  onMount(async () => {
    log.info("DebtorSelector: fetching indexed debtors");
    await fetchDebtors();
    let initialDebtorId = preSelectedDebtorId;
    if (initialDebtorId) {
      log.info(
        "DebtorSelector: fetching initial debtor with id",
        initialDebtorId,
      );
      await fetchSelectedDebtor(initialDebtorId);
    }
  });

  // Map debtors to SelectItem
  $: debtors,
    (debtorItems = debtors.map(
      (debtor) =>
        `${debtor?.debtor.name} (${
          isPersonVariant(debtor?.debtor.sid)
            ? debtor?.debtor.sid.person
            : debtor?.debtor.sid.organisation
        })`,
    ));
  // Handle selected debtor
  $: {
    if ($selectedDebtorIdx !== undefined) {
      fetchSelectedDebtor(debtors[$selectedDebtorIdx].id);
      $selectedDebtorIdx = undefined;
    }
  }
</script>

<!-- Debtor -->
<div class="max-w-[460px]">
  <h3 class="text-[#58686A] text-[17px]">{label}</h3>
  <p class="text-[#58686A] text-[15px] mt-[16px] mb-[8px]">
    Ange parkör ({debtorHits} träffar)
  </p>
  <InputDropdown
    title="Sök med namn, personnummer eller epost"
    name="item-picker-debtor"
    {disabled}
    bind:items={debtorItems}
    selectedIdx={selectedDebtorIdx}
    customClass="mt-[4px]"
    reduceResultsOnTyping={false}
    onEnterPressed={(value) => {
      fetchDebtors(value);
    }}
    onClearClicked={() => {
      fetchDebtors("");
    }}
    changeValueOnSelect={false}
  />

  <!-- Selected debtor -->
  {#if selectedDebtor && selectedDebtorIsLoading === false}
    <!-- Selection -->
    <div class="mt-[16px] mb-[8px]">
      <span class="text-[#58686A] text-[15px]">Vald parkör</span>
      <button
        class="inline py-[6px] pr-[8px] float-end pl-[24px]"
        on:click={() => {
          if (disabled) {
            Toast.info("Parkören är låst");
            return;
          }
          selectedDebtor = undefined;
        }}
      >
        <img alt="cross" src="/icons/cross.svg" class="" />
      </button>
    </div>
    <div in:fade={{ duration: 500 }} out:fade={{ duration: 100 }}>
      <ListNavigationItem
        title={isPersonVariant(selectedDebtor.sid)
          ? "Personnummer"
          : "Organisationsnummer"}
        textRight={isPersonVariant(selectedDebtor.sid)
          ? selectedDebtor.sid.person
          : selectedDebtor.sid.organisation}
        isFirst={true}
      />
      <ListNavigationItem title="Namn" textRight={selectedDebtor.name} />
      <ListNavigationItem
        title="E-post kontakt"
        textRight={selectedDebtor.contactEmail ?? "-"}
        isLast={true}
      />
    </div>

    <!-- Status -->
    <p class="text-[#58686A] text-[15px] mt-[16px] mb-[8px]">Parkörstatus</p>
    {#if !selectedDebtor.deliveryMethod}
      <!-- Status: Deliverymethod failed -->
      <p class="text-[#E2533F] text-[17px] font-semibold">
        <img
          alt="cross"
          src="/icons/cross.svg"
          class="inline -mt-[4px] w-[18px] mr-[8px]"
        />Leveransmetod saknas
      </p>
    {/if}
  {:else if selectedDebtorIsLoading === true}
    <LoadingIndicator visible={true} customClass={"my-[24px]"} />
  {/if}
</div>
